import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  BudgetSubmitReference,
  BudgetValue,
  DIRECT_COST_QUESTION_ID,
  FINAL_BUDGET_QUESTION_ID,
  IN_KIND_DONATIONS_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
  defaultBudgetValue,
  getBudgetDollarAmount,
  getFinalizedBudget,
  getGetBudgetQuestionProperty,
  getTotalBudget,
  saveBudgetAmountValue,
  saveFinalBudget,
} from './utils';
import { BottomBar } from '../BottomBar';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { CostContainer, directCostCheckboxText } from './ProgramCost';
import TrueImpactDollarField from 'shared/design-system/components/TrueImpactDollarField';
import RelevancyCheckbox, { defaultCostValue } from './RelevancyCheckbox';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactCapsule from 'shared/design-system/components/TrueImpactCapsule';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { overheadCostCheckboxText } from './BudgetOverheadCost';
import { numberWithCommas } from 'shared/utils/numberUtils';
import {
  FeedbackUpdateAnswerResponse,
  updateAnswer,
} from '../../../NPOFeedback/MakeEdits/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { parsePeriodValuesString } from '../SectionPeriod/utils';
import { formattedDate } from 'shared/utils/date';
import {
  DATES_QUESTION_ID_1,
  DATES_QUESTION_ID_2,
} from '../ReportFinalize/answersUtil';
import { updateIndicatorAnswer } from '../ImpactIndicator/api';
import { GuidanceTextResponse } from '../utils';
import ShowGuidanceText from '../Review/ReportCustomQuestionType/ShowGuidanceText';
import { logError } from 'shared/utils/logError';
import { FeedbackCommentBodyWithIndex } from '../../../NPOFeedback/utils';
import ShowIndicatorFeedback from '../../../NPOFeedback/MakeEdits/components/ImpactTable/ShowIndicatorFeedback';
import { useReport } from 'shared/providers/ReportProvider';
import AllocationWarning from './AllocationWarning';
import JSON5 from 'json5';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import { getBudgetFlag } from './budgetFlagUtils';
import BudgetFlagBox from './BudgetFlagBox';
import BudgetFlagModal from './BudgetFlagModal';
import { updateReportLevel1Budget } from '../Review/api';

interface BudgetSummaryProps {
  guidanceText: GuidanceTextResponse;
  initialValue?: string | BudgetValue;
  reportId: string | number;
  isReportPassed?: boolean;
  hideBottomBar: boolean | undefined;
  values?: { [id: number]: string | undefined };
  feedbackByQuestionId: {
    [questionId: number]: FeedbackCommentBodyWithIndex[];
  };
  onPrevious: () => unknown;
  onNext: (value: string) => unknown;
  // Optional reactive emitter for parents that persist through their own
  // footer instead of this component's BottomBar. Fires only after user
  // interaction, never on initial mount.
  onValueChange?: (value: string) => void;
}

const getTrueValue = (dollar: string | undefined): string | undefined => {
  // return dollar === defaultCostValue ? undefined : dollar;
  return dollar;
};

const getValue = (
  questionId: number,
  values: BudgetSummaryProps['values'],
  initial: ReturnType<typeof parseInitialValue>,
  isReportPassed?: boolean,
) => {
  const budgetKey = getGetBudgetQuestionProperty(questionId) || '';
  const initialValue =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getTrueValue(values?.[budgetKey as any]) ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getTrueValue(initial?.[budgetKey as any]) ||
    getBudgetDollarAmount(values?.[questionId] || initial[questionId]);
  const type = getGetBudgetQuestionProperty(questionId);
  if (isReportPassed && values && type) {
    return (
      getFinalizedBudget(values[FINAL_BUDGET_QUESTION_ID])?.[type] ||
      initialValue ||
      defaultBudgetValue
    );
  }
  if (!values) return defaultBudgetValue;
  const raw = values[questionId];
  return (
    (raw ? getBudgetDollarAmount(raw) : initialValue) || defaultBudgetValue
  );
};

const setValues = (
  directCost: string,
  inKindDonations: string,
  overheadCost: string,
): {
  [id: number]: string | undefined;
} => {
  const result: BudgetSummaryProps['values'] = {};
  result[DIRECT_COST_QUESTION_ID] = saveBudgetAmountValue(directCost);
  result[IN_KIND_DONATIONS_QUESTION_ID] = saveBudgetAmountValue(
    inKindDonations,
  );
  result[OVERHEAD_COST_QUESTION_ID] = saveBudgetAmountValue(overheadCost);
  return result;
};

const StyledDiv = styled.div`
  margin-top: 48px;
  margin-bottom: 150px;
  .heading,
  .sub-heading {
    text-align: center;
  }
  .sub-heading {
    margin-top: ${SPACERS.small};
    margin-bottom: ${SPACERS.small};
  }
  .period-heading,
  .period-heading-details {
    text-align: center;
  }
  .period-heading {
    margin-bottom: ${SPACERS.small};
  }
  .budget-summary-table {
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 4px;
    margin-top: ${SPACERS.extraLarge};
    border-left: 1px solid ${COLORS.grayLight};
    border-right: 1px solid ${COLORS.grayLight};
    .budget-summary-row {
      display: flex;
      padding-left: ${SPACERS.extraLarge};
      padding-right: ${SPACERS.extraLarge};
    }
    .cost-container {
      padding-top: ${SPACERS.large};
      margin-bottom: ${SPACERS.small};
    }
    .cost-container,
    .budget-summary-row-child {
      flex: 1;
      padding-top: ${SPACERS.medium};
    }
    .budget-summary-row-child-extra-padding {
      padding-left: ${SPACERS.medium};
    }
    .budget-summary-row-child-xx-padding {
      padding-left: ${SPACERS.large};
    }
    .budget-summary-table-header {
      height: 87px;
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
      background-color: ${COLORS.grayLight};
    }
    .budget-summary-table-footer {
      height: 69px;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      color: ${COLORS.whiteBase};
      background-color: ${COLORS.grayDarkest};
    }
    .budget-summary-border-bottom {
      border-bottom: 1px solid ${COLORS.grayLight};
    }
  }
`;

const parseInitialValue = (
  initial: string | BudgetValue | undefined,
): NonNullable<BudgetSummaryProps['values']> => {
  if (!initial || typeof initial !== 'string') return {};
  try {
    const parsed = JSON5.parse(initial);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed;
    }
  } catch (e) {
    logError('Failed to parse initial value:', e);
  }
  return {};
};

const BudgetSummary = React.forwardRef<
  BudgetSubmitReference,
  BudgetSummaryProps
>(
  (
    {
      reportId,
      values,
      guidanceText,
      isReportPassed,
      initialValue,
      hideBottomBar,
      feedbackByQuestionId,
      onPrevious,
      onNext,
      onValueChange,
    },
    forwardRef,
  ) => {
    const didMountRef = React.useRef(false);
    const { totalAllocationAmount, report, handleReloadReport } = useReport();
    const levelOneFeedbackEnabled = useFeatureFlags('levelOneFeedback');
    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [showFlagModal, setShowFlagModal] = useState<boolean>(false);
    const date = values?.[DATES_QUESTION_ID_1] || values?.[DATES_QUESTION_ID_2];
    const initial = useMemo(() => parseInitialValue(initialValue), [
      initialValue,
    ]);

    const client = useClient();
    const [, makeRequest] = useGraphQLRequest<FeedbackUpdateAnswerResponse>();
    const [directCost, setDirectCost] = useState<string>(
      getValue(DIRECT_COST_QUESTION_ID, values, initial, isReportPassed),
    );
    const [inKindDonations, setInKindDonations] = useState<string>(
      getValue(IN_KIND_DONATIONS_QUESTION_ID, values, initial, isReportPassed),
    );
    const [overheadCost, setOverheadCost] = useState<string>(
      getValue(OVERHEAD_COST_QUESTION_ID, values, initial, isReportPassed),
    );

    useEffect(() => {
      if (!onValueChange) return;
      if (!didMountRef.current) {
        didMountRef.current = true;
        return;
      }
      onValueChange(
        JSON5.stringify(setValues(directCost, inKindDonations, overheadCost)),
      );
    }, [directCost, inKindDonations, overheadCost]);

    const handleCloseWarning = useCallback(() => {
      setShowWarning(false);
    }, [setShowWarning]);

    const totalBudget = getTotalBudget(
      directCost,
      inKindDonations,
      overheadCost,
    );

    // Level 1 feedback: the program budget is compared against the org's most
    // recent IRS Form 990 expenses. No filing on record means nothing to
    // compare against, so no flag and no block.
    const savedBudgetNote = report.data?.level1Budget || '';
    const hasBudgetNoteRef = React.useRef<boolean>(Boolean(savedBudgetNote));
    const advanceAfterNoteRef = React.useRef<boolean>(false);
    useEffect(() => {
      if (savedBudgetNote) {
        hasBudgetNoteRef.current = true;
      }
    }, [savedBudgetNote]);

    const budgetFlag = levelOneFeedbackEnabled
      ? getBudgetFlag(totalBudget, report.data?.orgAnnualExpenses)
      : undefined;
    const needsBudgetExplanation =
      Boolean(budgetFlag) && !savedBudgetNote && !hasBudgetNoteRef.current;

    const saveAndContinue = useCallback(() => {
      if (!client) return;

      if (isReportPassed) {
        const result = JSON5.stringify(
          saveFinalBudget(directCost, overheadCost, inKindDonations),
        );
        makeRequest(
          updateIndicatorAnswer(
            client,
            undefined,
            true,
            FINAL_BUDGET_QUESTION_ID,
            reportId,
            result,
          ),
        );
        onNext(result);
        return;
      }

      const result = setValues(directCost, inKindDonations, overheadCost);
      makeRequest(
        updateAnswer(client, {
          reportId: reportId as number,
          questionId: DIRECT_COST_QUESTION_ID,
          answerId: undefined,
          answer: result[DIRECT_COST_QUESTION_ID]!,
        }),
      );
      makeRequest(
        updateAnswer(client, {
          reportId: reportId as number,
          questionId: IN_KIND_DONATIONS_QUESTION_ID,
          answerId: undefined,
          answer: result[IN_KIND_DONATIONS_QUESTION_ID]!,
        }),
      );
      makeRequest(
        updateAnswer(client, {
          reportId: reportId as number,
          questionId: OVERHEAD_COST_QUESTION_ID,
          answerId: undefined,
          answer: result[OVERHEAD_COST_QUESTION_ID]!,
        }),
      );
      onNext(JSON5.stringify(result));
    }, [
      client,
      directCost,
      inKindDonations,
      overheadCost,
      isReportPassed,
      makeRequest,
      onNext,
      reportId,
    ]);

    const handleNext = useCallback(() => {
      if (!client) return;
      if (!(directCost && inKindDonations && overheadCost)) return;

      if (totalBudget < totalAllocationAmount) {
        setShowWarning(true);
        return;
      }

      if (needsBudgetExplanation) {
        advanceAfterNoteRef.current = true;
        setShowFlagModal(true);
        return;
      }

      saveAndContinue();
    }, [
      client,
      directCost,
      inKindDonations,
      overheadCost,
      totalBudget,
      totalAllocationAmount,
      needsBudgetExplanation,
      saveAndContinue,
    ]);

    const handleCloseFlagModal = useCallback(() => {
      advanceAfterNoteRef.current = false;
      setShowFlagModal(false);
    }, []);

    const handleConfirmFlagNote = useCallback(
      async (note: string) => {
        if (!client) return;
        // Only continue to the next question when the popup was triggered by
        // Save & Continue; explaining straight from the box just dismisses it.
        const shouldAdvance = advanceAfterNoteRef.current;
        advanceAfterNoteRef.current = false;
        setShowFlagModal(false);
        hasBudgetNoteRef.current = true;
        try {
          await updateReportLevel1Budget(client, reportId, note);
          handleReloadReport();
        } catch (e) {
          logError('Failed to save budget explanation:', e);
        }
        if (shouldAdvance) {
          saveAndContinue();
        }
      },
      [client, reportId, handleReloadReport, saveAndContinue],
    );

    useImperativeHandle(forwardRef, () => ({
      error: () => {
        if (
          !(
            Boolean(directCost) &&
            Boolean(inKindDonations) &&
            Boolean(overheadCost)
          )
        ) {
          return 'Please enter valid cost in each field.';
        }
        if (needsBudgetExplanation) {
          return 'Please review the budget check below and add a short explanation to continue.';
        }
        return undefined;
      },
      handleSubmit: () =>
        JSON5.stringify({
          directCost: directCost,
          overheadCost: overheadCost,
          inKindDonations: inKindDonations,
        }),
    }));

    const period =
      typeof date === 'string' ? parsePeriodValuesString(date) : undefined;

    const directCostComments =
      feedbackByQuestionId[DIRECT_COST_QUESTION_ID] || [];
    const overheadCostComments =
      feedbackByQuestionId[OVERHEAD_COST_QUESTION_ID] || [];
    const inKindDonationComments =
      feedbackByQuestionId[IN_KIND_DONATIONS_QUESTION_ID] || [];

    return (
      <>
        <StyledDiv>
          <TrueImpactText variant="heading-page" className="heading">
            Please review your program’s budget.
          </TrueImpactText>
          <ShowGuidanceText
            sectionId="budget"
            questionId={80011}
            guidanceText={guidanceText}
          />
          <TrueImpactText className="sub-heading">
            Feel free to make changes if need be.
          </TrueImpactText>
          {period && period.startDate && period.endDate && (
            <>
              <TrueImpactText className="period-heading">
                Reporting time period:
              </TrueImpactText>
              <TrueImpactText className="period-heading-details">
                <strong>{formattedDate(period.startDate)}</strong> to{' '}
                <strong>{formattedDate(period.endDate)}</strong>
              </TrueImpactText>
            </>
          )}
          <div className="budget-summary-table">
            <div className="budget-summary-table-header budget-summary-row">
              <div className="budget-summary-row-child">
                <TrueImpactCapsule>Expense/Donation Type</TrueImpactCapsule>
              </div>
              <div className="budget-summary-row-child">
                <TrueImpactCapsule>Amount Budgeted</TrueImpactCapsule>
              </div>
            </div>
            <div className="budget-summary-table-body">
              <div className="budget-summary-row budget-summary-border-bottom">
                <TrueImpactText
                  variant="body-bold"
                  className="budget-summary-row-child"
                >
                  Direct Cost
                </TrueImpactText>
                <CostContainer className="cost-container">
                  <TrueImpactDollarField
                    value={directCost}
                    onChange={setDirectCost}
                    disabled={directCost === defaultCostValue}
                    toFixedDecimal={2}
                    placeholder="Total direct cost"
                    className="cost-field"
                  />
                  {directCost === defaultCostValue && (
                    <RelevancyCheckbox
                      value={directCost}
                      handleValue={setDirectCost}
                      text={directCostCheckboxText}
                    />
                  )}
                  <ShowIndicatorFeedback
                    defaultOpen={false}
                    comments={directCostComments}
                  />
                </CostContainer>
              </div>
              <div className="budget-summary-row budget-summary-border-bottom">
                <TrueImpactText
                  variant="body-bold"
                  className="budget-summary-row-child"
                >
                  Overhead Cost
                </TrueImpactText>
                <CostContainer className="cost-container">
                  <TrueImpactDollarField
                    value={overheadCost}
                    onChange={setOverheadCost}
                    disabled={overheadCost === defaultCostValue}
                    toFixedDecimal={2}
                    placeholder="Total overhead cost"
                    className="cost-field"
                  />
                  <RelevancyCheckbox
                    value={overheadCost}
                    handleValue={setOverheadCost}
                    text={overheadCostCheckboxText}
                  />
                  <ShowIndicatorFeedback
                    defaultOpen={false}
                    comments={overheadCostComments}
                  />
                </CostContainer>
              </div>
              <div className="budget-summary-row budget-summary-border-bottom">
                <TrueImpactText
                  variant="body-bold"
                  className="budget-summary-row-child"
                >
                  In-kind Donations
                </TrueImpactText>
                <CostContainer className="cost-container">
                  <TrueImpactDollarField
                    value={inKindDonations}
                    onChange={setInKindDonations}
                    disabled={inKindDonations === defaultCostValue}
                    toFixedDecimal={2}
                    placeholder="Total in-kind donations"
                    className="cost-field"
                  />
                  <RelevancyCheckbox
                    value={inKindDonations}
                    handleValue={setInKindDonations}
                    text="In-kind Donations are not relevant to my program"
                  />
                  <ShowIndicatorFeedback
                    defaultOpen={false}
                    comments={inKindDonationComments}
                  />
                </CostContainer>
              </div>
            </div>
            <div className="budget-summary-table-footer budget-summary-row">
              <TrueImpactText
                color="whiteBase"
                variant="body-bold"
                className="budget-summary-row-child"
              >
                Total Budget
              </TrueImpactText>
              <TrueImpactText
                color="whiteBase"
                className="budget-summary-row-child budget-summary-row-child-xx-padding"
              >
                ${numberWithCommas(totalBudget.toFixed(2))}
              </TrueImpactText>
            </div>
          </div>
          {budgetFlag && (
            <BudgetFlagBox
              flag={budgetFlag}
              note={savedBudgetNote}
              onExplain={() => setShowFlagModal(true)}
            />
          )}
        </StyledDiv>
        <BudgetFlagModal
          open={showFlagModal}
          flag={budgetFlag}
          initialNote={savedBudgetNote}
          onClose={handleCloseFlagModal}
          onConfirm={handleConfirmFlagNote}
        />
        <AllocationWarning
          open={showWarning}
          totalReportBudget={totalBudget}
          totalAllocationAmount={totalAllocationAmount}
          handleClose={handleCloseWarning}
        />
        {!hideBottomBar && (
          <BottomBar
            disabled={false}
            hideBack={false}
            hideNext={!(directCost && inKindDonations && overheadCost)}
            handleBack={onPrevious}
            handleNext={handleNext}
            backTitle="Back"
            nextTitle="Save & Continue"
          />
        )}
      </>
    );
  },
);

export default BudgetSummary;
